// src/pages/HomePage.js
// Updated: 06/20/2026

import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore'; 
import { format } from 'date-fns';
import { FaCalendarAlt, FaMapMarkerAlt, FaCalendarPlus, FaBullhorn, FaHistory, FaClock, FaInfoCircle } from 'react-icons/fa';

const HomePage = () => {
  const { familyData, isHost } = useFamily();
  const [events, setEvents] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]); // Array of announcements
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!familyData) return;
    
    // Fetch ALL events ordered by start date so we can categorize them
    const eventsRef = collection(db, `families/${familyData.id}/events`);
    const qEvents = query(eventsRef, orderBy('startDate', 'desc'));
    const unsubEvents = onSnapshot(qEvents, (snap) => { 
        setEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
        setLoading(false); 
    });
    
    // Fetch latest 3 announcements for the updates block
    const announcementsRef = collection(db, `families/${familyData.id}/announcements`);
    const qAnnouncements = query(announcementsRef, orderBy('createdAt', 'desc'), limit(3));
    const unsubAnnouncements = onSnapshot(qAnnouncements, (snap) => {
        setRecentAnnouncements(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubEvents(); unsubAnnouncements(); };
  }, [familyData]);

  // Categorize events
  const { upcomingEvents, ongoingEvents, pastEvents } = useMemo(() => {
      const now = new Date();
      
      const upcoming = events.filter(e => e.startDate.toDate() > now)
                             .sort((a,b) => a.startDate.toDate() - b.startDate.toDate()); // Nearest first
      
      const ongoing = events.filter(e => e.startDate.toDate() <= now && e.endDate.toDate() >= now)
                            .sort((a,b) => a.startDate.toDate() - b.startDate.toDate());
                            
      const past = events.filter(e => e.endDate.toDate() < now)
                         .sort((a,b) => b.startDate.toDate() - a.startDate.toDate()); // Most recent past first

      return { upcomingEvents: upcoming, ongoingEvents: ongoing, pastEvents: past };
  }, [events]);

  const EventCard = ({ event, badge, grayscale }) => (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 ${grayscale ? 'opacity-80 grayscale-[0.4]' : ''}`}>
      <div className="p-6 relative">
        {badge && <span className="absolute top-4 right-4 bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1"><FaClock/> {badge}</span>}
        <h3 className={`text-xl font-bold ${grayscale ? 'text-gray-700' : 'text-primary'}`}>{event.eventName}</h3>
        <div className="text-gray-500 text-sm mt-2 space-y-2">
          <p className="flex items-center gap-2"><FaCalendarAlt /><span>{format(event.startDate.toDate(), 'MMMM d, yyyy')}</span></p>
          <p className="flex items-center gap-2"><FaMapMarkerAlt /><span>{event.location}</span></p>
        </div>
        <p className="mt-4 text-gray-600 line-clamp-3">{event.description}</p>
      </div>
      <div className="bg-gray-50 px-6 py-4 border-t">
        <Link to={`/event/${event.id}`} className={`font-semibold ${grayscale ? 'text-gray-600 hover:text-gray-900' : 'text-primary hover:text-blue-700'}`}>
            View Details &rarr;
        </Link>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0 space-y-10">
        
        {/* Welcome Section */}
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-neutral-gray">Welcome to the {familyData?.familyName} Hub!</h1>
                    <p className="mt-2 text-gray-600">All your family reunion information, all in one place.</p>
                </div>
                {isHost && (<Link to="/dashboard" className="px-6 py-2 bg-cta-orange text-white rounded-md hover:bg-orange-700 text-sm font-medium shadow whitespace-nowrap">Host Dashboard</Link>)}
            </div>
        </div>

        {/* Updates from the Host Block */}
        {recentAnnouncements.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg shadow-sm">
                <h2 className="text-2xl font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <FaInfoCircle className="text-primary"/> Updates from the Host
                </h2>
                <div className="space-y-4">
                    {recentAnnouncements.map((announcement) => (
                        <div key={announcement.id} className="bg-white p-4 rounded-md shadow-sm border-l-4 border-primary">
                            <div className="flex justify-between items-start">
                                <h3 className="font-bold text-gray-800 text-lg">{announcement.title}</h3>
                                {/* FIXED: Safe fallback for createdAt */}
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                    {announcement.createdAt ? format(announcement.createdAt.toDate(), 'MMM d, yyyy') : 'Just now'}
                                </span>
                            </div>
                            <p className="text-gray-700 mt-2 whitespace-pre-wrap">{announcement.message}</p>
                            <p className="text-xs text-gray-400 mt-3 font-medium">- {announcement.author}</p>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {loading ? (
            <div className="bg-white p-6 rounded-lg shadow-md text-center"><p className="text-gray-500 animate-pulse">Loading family events...</p></div>
        ) : (
            <>
                {/* Ongoing Events */}
                {ongoingEvents.length > 0 && (
                    <div>
                        <h2 className="text-2xl font-semibold text-neutral-gray mb-4 flex items-center gap-2">Happening Now</h2>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {ongoingEvents.map(event => <EventCard key={event.id} event={event} badge="Happening Now" />)}
                        </div>
                    </div>
                )}

                {/* Upcoming Events */}
                <div>
                    <h2 className="text-2xl font-semibold text-neutral-gray mb-4">Upcoming Events</h2>
                    {upcomingEvents.length > 0 ? (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {upcomingEvents.map(event => <EventCard key={event.id} event={event} />)}
                        </div>
                    ) : (
                        <div className="bg-white p-6 rounded-lg shadow-md text-center py-12">
                            <FaCalendarPlus className="mx-auto text-5xl text-gray-300"/>
                            <h3 className="mt-4 text-lg font-medium text-gray-800">No Upcoming Events</h3>
                            {isHost ? (<p className="text-gray-500 mt-2">Head to the dashboard to create one and get the planning started!</p>) : (<p className="text-gray-500 mt-2">Check back soon for reunion updates from your family host.</p>)}
                        </div>
                    )}
                </div>

                {/* Past Events */}
                {pastEvents.length > 0 && (
                    <div>
                        <h2 className="text-2xl font-semibold text-neutral-gray mb-4 flex items-center gap-2">Past Memories <FaHistory className="text-gray-400 text-lg"/></h2>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {pastEvents.map(event => <EventCard key={event.id} event={event} grayscale={true} />)}
                        </div>
                    </div>
                )}
            </>
        )}
      </div>
    </Layout>
  );
};

export default HomePage;